import { z } from "zod";
import { EmptyPayloadSchema, OkDataSchema } from "./common.js";
import { AgentIdSchema, IdSchema, IsoDateTimeSchema } from "./primitives.js";
import { AgentTeamIdSchema } from "./teams.js";

const RESERVED_HANDLES = new Set(["everyone", "here", "channel", "admin"]);

export const AgentHandleSchema = z
  .string()
  .min(2, "handle must be at least 2 characters")
  .max(32, "handle must be at most 32 characters")
  .regex(
    /^[a-z][a-z0-9-]*$/,
    "handle must be lowercase letters, digits, or hyphens, starting with a letter",
  )
  .refine((value) => !RESERVED_HANDLES.has(value), "handle is reserved");

const AgentVerbositySchema = z.enum(["concise", "balanced", "verbose"]);

const AgentReasoningTierSchema = z.enum(["light", "standard", "advanced"]);

/**
 * Per-agent ACL (channel-agnostic). `allowedUsers` and `allowedChannels`
 * are gone — strict per-user access lives in Gateway SQLite via
 * `agent_grants`. `workspaceWide` remains as the "any org member can
 * talk to this agent" opt-out.
 */
const AgentAclSchema = z
  .object({
    workspaceWide: z.boolean(),
  })
  .strict();
const AgentAclResponseSchema = AgentAclSchema.passthrough();

const AgentSummarySchema = z
  .object({
    id: AgentIdSchema,
    displayName: z.string().min(1),
    jobTitle: z.string().optional(),
    description: z.string().optional(),
    iconUrl: z.string().nullable().optional(),
    enabled: z.boolean(),
    toneMode: z.string().optional(),
    handle: AgentHandleSchema.nullable().optional(),
    verbosity: AgentVerbositySchema.nullable().optional(),
    /** Per-agent reasoning-tier override; null/absent inherits the default `standard` tier. */
    reasoningTier: AgentReasoningTierSchema.nullable().optional(),
    presetId: z.string().nullable().optional(),
    /**
     * Specialist-template pin. The Gateway persists provenance only — template
     * content (prompt, starter skills, recommended connectors) lives in the
     * Hub registry. Absent/null = Custom specialist. The legacy `presetId`
     * field above is read-tolerance only; Hub maps it to
     * `templateId = presetId, templateVersion = 1` at read time and
     * `templateId` wins when both are present.
     */
    templateId: z.string().nullable().optional(),
    templateVersion: z.number().int().positive().nullable().optional(),
    /**
     * The one Agent Team this agent belongs to. `null`/absent means Unassigned.
     * Lives on the summary (not just the detail) so list-driven surfaces — the
     * team-grouped Specialists list above all — can group `agent.list` rows
     * without a per-agent fetch.
     */
    teamId: AgentTeamIdSchema.nullable().optional(),
    /**
     * Display name of `teamId`'s team. Stamped by Hub on top of Gateway's
     * `agent.list` / `agent.get` response; the Gateway never sends or reads it.
     * It exists so the team-grouped Specialists list can label its sections
     * without the viewer reading the admin-only team registry — a viewer
     * therefore learns only the names of teams their own visible agents are in.
     * Absent when the agent has no team, or when the registry was unreadable.
     */
    teamName: z.string().min(1).optional(),
    lastActiveAt: IsoDateTimeSchema.optional(),
    /**
     * Per-agent "can the viewer edit this agent?" predicate. Stamped
     * by Hub on top of Gateway's `agent.list` / `agent.get` response;
     * Gateway is agnostic about who's viewing. Owner and Editor are true;
     * User and unassigned viewers are false regardless of platform role.
     */
    viewerCanEdit: z.boolean().optional(),
    /**
     * Per-agent "can the viewer chat with this agent?" predicate. Stamped by
     * Hub with channel delivery's reachability rule: an assigned specialist
     * role or `acl.workspaceWide`; an Administrator also reaches an ownerless
     * specialist for recovery.
     */
    viewerCanChat: z.boolean().optional(),
    /**
     * Archival lifecycle. `archived` is the derived flag the UI gates on;
     * `archivedAt` records when the agent was retired. Absent on active agents.
     */
    archived: z.boolean().optional(),
    archivedAt: IsoDateTimeSchema.nullable().optional(),
    /**
     * "Has this agent ever held a conversation?" — stamped on the detail
     * response. Once true, the Delete affordance is gone and Archive is the
     * only retirement path.
     */
    hasConversations: z.boolean().optional(),
    /** Viewer lifecycle predicates, stamped by Gateway on the detail response. */
    viewerCanArchive: z.boolean().optional(),
    viewerCanDelete: z.boolean().optional(),
  })
  .passthrough();

export const AgentDetailSchema = AgentSummarySchema.extend({
  corePrompt: z.string().optional(),
  iconEmoji: z.string().optional(),
  acl: AgentAclResponseSchema,
  enabledSkills: z.array(IdSchema),
  createdAt: IsoDateTimeSchema.optional(),
  updatedAt: IsoDateTimeSchema.optional(),
}).passthrough();

export const AgentChatTargetSchema = z
  .object({
    id: AgentIdSchema,
    displayName: z.string().min(1),
    jobTitle: z.string().optional(),
    description: z.string().optional(),
    iconUrl: z.string().nullable().optional(),
    enabled: z.boolean(),
    viewerCanChat: z.literal(true),
  })
  .passthrough();

export const AgentCreateInputSchema = z
  .object({
    displayName: z.string().min(1),
    jobTitle: z.string().min(1),
    description: z.string().min(1),
    /** Omitted = Unassigned. The Gateway rejects an id that names no team. */
    teamId: AgentTeamIdSchema.optional(),
    toneMode: z.string().min(1),
    corePrompt: z.string().optional(),
    enabledSkills: z.array(IdSchema).optional(),
    handle: AgentHandleSchema.optional(),
    verbosity: AgentVerbositySchema.optional(),
    /** Explicit `null` inherits the default `standard` tier; absent leaves it unset. */
    reasoningTier: AgentReasoningTierSchema.nullable().optional(),
    // Legacy preset marker. No writer sends it anymore (the wizard pins
    // templateId/templateVersion instead); the field stays on the contract for
    // rolling-release tolerance until a Gateway-contract release removes it.
    presetId: z.string().min(1).optional(),
    // Both optional; the Custom flow omits both. Hub validates templateId
    // against its registry before the create reaches Gateway.
    templateId: z.string().min(1).optional(),
    templateVersion: z.number().int().positive().optional(),
  })
  .strict();

export const AgentUpdateInputSchema = AgentCreateInputSchema.partial()
  .extend({
    agentId: AgentIdSchema,
    enabled: z.boolean().optional(),
    acl: AgentAclSchema.partial().optional(),
    /** Explicit `null` unassigns the agent; omitting the key leaves it unchanged. */
    teamId: AgentTeamIdSchema.nullable().optional(),
  })
  .strict()
  .superRefine((value, ctx) => {
    const { agentId: _agentId, ...updates } = value;
    if (Object.keys(updates).length === 0) {
      ctx.addIssue({
        code: "custom",
        message: "agent update requires at least one mutable field",
      });
    }
  });

export const AgentListPayloadSchema = EmptyPayloadSchema;
export const AgentListDataSchema = z.object({ agents: z.array(AgentSummarySchema) }).passthrough();
export const AgentGetPayloadSchema = z.object({ agentId: AgentIdSchema }).strict();
export const AgentGetDataSchema = z.object({ agent: AgentDetailSchema }).passthrough();
export const AgentChatTargetDataSchema = z.object({ agent: AgentChatTargetSchema }).passthrough();
export const AgentCreatePayloadSchema = AgentCreateInputSchema;
export const AgentCreateDataSchema = AgentGetDataSchema;
export const AgentUpdatePayloadSchema = AgentUpdateInputSchema;
export const AgentUpdateDataSchema = AgentGetDataSchema;
export const AgentDeletePayloadSchema = z.object({ agentId: AgentIdSchema }).strict();
export const AgentDeleteDataSchema = OkDataSchema.passthrough();

export const AgentArchivePayloadSchema = z.object({ agentId: AgentIdSchema }).strict();
export const AgentArchiveDataSchema = OkDataSchema.passthrough();
export const AgentUnarchivePayloadSchema = AgentArchivePayloadSchema;
export const AgentUnarchiveDataSchema = OkDataSchema.passthrough();
export const AgentArchivedListDataSchema = z
  .object({ agents: z.array(AgentSummarySchema) })
  .passthrough();

export const AgentAvatarSetUrlPayloadSchema = z
  .object({
    agentId: AgentIdSchema,
    iconUrl: z
      .string()
      .url()
      .refine((value) => {
        try {
          return new URL(value).protocol === "https:";
        } catch {
          return false;
        }
      }, "iconUrl must use HTTPS")
      .nullable(),
  })
  .strict();
export const AgentAvatarSetUrlDataSchema = OkDataSchema.passthrough();

export type AgentSummary = z.infer<typeof AgentSummarySchema>;
export type AgentDetail = z.infer<typeof AgentDetailSchema>;
export type AgentChatTarget = z.infer<typeof AgentChatTargetSchema>;
export type AgentCreateInput = z.infer<typeof AgentCreateInputSchema>;
export type AgentUpdateInput = z.infer<typeof AgentUpdateInputSchema>;
export type AgentVerbosity = z.infer<typeof AgentVerbositySchema>;
export type AgentReasoningTier = z.infer<typeof AgentReasoningTierSchema>;
